import type { FC } from "react";

import { nonWhiteSpaceOr } from "@arti/utilities";

import type { SourceControlRepository } from "../../types";

import { Button } from "../common/Button";
import { IndexFreshnessBadge } from "../common/IndexFreshnessBadge";
import { SectionHeader } from "../common/SectionHeader";
import { Text } from "../common/Text";

interface Props {
  repository: SourceControlRepository;
  isExpanded: boolean;
  onToggle: () => void;
  onDelete: () => void;
}

const RepositoryIndexStatus: FC<{ repository: SourceControlRepository }> = ({
  repository: repo,
}) => {
  let tooltip = "Not indexed yet";
  if (repo.index_freshness) {
    if (repo.index_freshness.state === "OK") {
      tooltip = "Indexed";
    } else if (repo.index_freshness.state === "STALE") {
      tooltip =
        repo.index_freshness.commits_behind !== null
          ? `${repo.index_freshness.commits_behind} commits behind`
          : "Stale";
    } else if (repo.index_freshness.state === "NONE") {
      tooltip = "Not indexed";
    }

    if (repo.last_indexed_at) {
      tooltip += ` (Last indexed: ${new Date(repo.last_indexed_at * 1000).toLocaleString()})`;
    }
  } else if (repo.last_indexed_at) {
    tooltip = `Indexed: ${new Date(repo.last_indexed_at * 1000).toLocaleString()}`;
  }

  return (
    <span className="flex items-center" title={tooltip}>
      <IndexFreshnessBadge
        indexFreshness={repo.index_freshness}
        lastIndexedAt={repo.last_indexed_at}
      />
    </span>
  );
};

const RepositoryTopicsList: FC<{ topics: string[] }> = ({ topics }) => (
  <div className="flex flex-wrap gap-1">
    {topics.map((topic) => (
      <Text
        key={topic}
        variant="label"
        className="border border-slate-300 bg-slate-100 px-1.5 py-0.5 text-gray-700 dark:border-slate-700 dark:bg-arti-panel dark:text-gray-300"
      >
        {topic}
      </Text>
    ))}
  </div>
);

const RepositoryIds: FC<{ repository: SourceControlRepository }> = ({
  repository: repo,
}) => (
  <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-500 dark:text-white/40">
    <div>
      Repository ID: <span className="font-mono">{repo.repository_id}</span>
    </div>
    <div>
      Provider ID: <span className="font-mono">{repo.provider_id}</span>
    </div>
  </div>
);

const SourceControlRepositoryItem: FC<Props> = ({
  repository: repo,
  isExpanded,
  onToggle,
  onDelete,
}) => (
  <div className="rounded-none border border-slate-300 bg-slate-50/70 p-3 text-sm dark:border-slate-700 dark:bg-arti-panel">
    <div className="flex items-start justify-between gap-3">
      <Button
        type="button"
        onClick={onToggle}
        variant="ghost"
        size="sm"
        className="mt-1 h-6 w-6 p-0 text-gray-400 hover:bg-transparent hover:text-gray-600 dark:text-white/40 dark:hover:bg-transparent dark:hover:text-white/80"
      >
        {isExpanded ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m18 15-6-6-6 6" />
          </svg>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        )}
      </Button>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <div className="font-mono text-xs font-bold tracking-[0.08em] text-gray-900 dark:text-white">
            {repo.remote_slug}
          </div>
          {repo.language && (
            <Text
              variant="label"
              className="rounded-none border border-slate-300 bg-white px-1.5 py-0.5 text-gray-600 dark:border-slate-700 dark:bg-arti-background dark:text-gray-300"
            >
              {repo.language}
            </Text>
          )}
        </div>
        {repo.description && (
          <Text as="div" className="mt-0.5 line-clamp-1">
            {repo.description}
          </Text>
        )}
        <Text as="div" className="mt-1 space-y-1">
          <div className="flex gap-3">
            <span>
              Branch: {nonWhiteSpaceOr(repo.default_branch, "default")}
            </span>
            <RepositoryIndexStatus repository={repo} />
          </div>
          <div className="flex gap-2">
            <Text
              variant="label"
              className={
                repo.is_required
                  ? "text-amber-600 dark:text-amber-400"
                  : "text-gray-600 dark:text-gray-400"
              }
            >
              {repo.is_required ? "Required" : "Available"}
            </Text>
            {repo.topics.length > 0 && (
              <span className="text-gray-400">
                [{repo.topics.slice(0, 3).join(", ")}
                {repo.topics.length > 3 ? "..." : ""}]
              </span>
            )}
          </div>
        </Text>

        {isExpanded && (
          <div className="mt-3 rounded-none border border-slate-300 bg-white/70 p-3 dark:border-slate-700 dark:bg-arti-background">
            <SectionHeader
              title="Indexed Metadata"
              variant="label"
              className="mb-2"
            />
            {repo.metadata ? (
              <pre className="overflow-x-auto whitespace-pre-wrap font-mono text-xs text-gray-700 dark:text-gray-300">
                {JSON.stringify(repo.metadata, null, 2)}
              </pre>
            ) : (
              <Text as="div" className="italic">
                No additional metadata indexed.
              </Text>
            )}

            {repo.topics.length > 0 && (
              <div className="mt-3">
                <SectionHeader
                  title="Topics"
                  variant="label"
                  className="mb-1"
                />
                <RepositoryTopicsList topics={repo.topics} />
              </div>
            )}
            <RepositoryIds repository={repo} />
          </div>
        )}
      </div>
      <Button variant="danger" size="sm" onClick={onDelete}>
        Remove
      </Button>
    </div>
  </div>
);

export default SourceControlRepositoryItem;
